/**
 * Dates and ranges the way a list shows them: "5 minuti fa" for today's work,
 * a plain date for last month's. Pure, with `now` passed in, so the tests do
 * not depend on the clock.
 */

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

export function relativeTime(timestamp: number | null | undefined, now: number, locale: string): string {
  if (timestamp === null || timestamp === undefined || !Number.isFinite(timestamp)) return '—'
  const diff = timestamp - now
  const abs = Math.abs(diff)
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  if (abs < MINUTE) return rtf.format(0, 'second')
  if (abs < HOUR) return rtf.format(Math.round(diff / MINUTE), 'minute')
  if (abs < DAY) return rtf.format(Math.round(diff / HOUR), 'hour')
  if (abs < 7 * DAY) return rtf.format(Math.round(diff / DAY), 'day')
  return absoluteDate(timestamp, locale)
}

export function absoluteDate(timestamp: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(timestamp))
}

export function absoluteDateTime(timestamp: number | null | undefined, locale: string): string {
  if (timestamp === null || timestamp === undefined || !Number.isFinite(timestamp)) return '—'
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(timestamp))
}

export interface PageWindow {
  /** The API cursor: an offset into the full result. */
  offset: number
  /** 1-based index of the first row shown; 0 when there is nothing. */
  from: number
  to: number
  pageCount: number
}

/** Page `page` (1-based) of `size` rows over `total` results. */
export function pageWindow(page: number, size: number, total: number): PageWindow {
  const safeSize = Math.max(1, Math.floor(size))
  const pageCount = Math.max(1, Math.ceil(total / safeSize))
  const safePage = Math.min(Math.max(1, Math.floor(page) || 1), pageCount)
  const offset = (safePage - 1) * safeSize
  if (total <= 0) return { offset: 0, from: 0, to: 0, pageCount: 1 }
  return { offset, from: offset + 1, to: Math.min(offset + safeSize, total), pageCount }
}

export const PAGE_SIZES = [25, 50, 100] as const

/** A page size from the URL, clamped to the ones offered; the API refuses more than 100. */
export function normalizePageSize(raw: string | null | undefined, fallback = 50): number {
  const value = Number(raw)
  return (PAGE_SIZES as readonly number[]).includes(value) ? value : fallback
}

/** Initials for an avatar without a picture. */
export function initials(name: string): string {
  const parts = name.replace(/@.*$/, '').split(/[\s._-]+/).filter(Boolean)
  const letters = (parts.length > 1 ? [parts[0], parts[parts.length - 1]] : [parts[0] ?? name])
    .map((part) => part?.[0] ?? '')
    .join('')
  return (letters || '?').toUpperCase()
}

/** "callToAction" → "Call to action", "meta.description" → "Meta description". */
export function humanize(key: string): string {
  const spaced = key
    .replace(/[._/-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase()
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : key
}

/** "Perché scegliere un CMS?" → "perche-scegliere-un-cms": the slug suggested from a title. */
export function slugify(text: string): string {
  return fold(text)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/g, '')
}

/** Case- and accent-insensitive "contains", for filters typed by a person. */
export function matchesQuery(query: string, ...values: (string | null | undefined)[]): boolean {
  const needle = fold(query.trim())
  if (!needle) return true
  return values.some((value) => value && fold(value).includes(needle))
}

function fold(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}
